<script lang="ts">
  /**
   * Header - Site navigation header.
   * Positioned above the grid lines.
   */
  import * as Sheet from '$shared/components/ui/sheet';
  import * as NavigationMenu from '#lib/components/ui/navigation-menu/index.js';
  import { navigationMenuTriggerStyle } from '#lib/components/ui/navigation-menu/navigation-menu-trigger.svelte';

  let mobileMenuOpen = $state(false);

  // Desktop navigation structure
  const productItems = [
    { href: '/product', title: 'Overview', description: 'The complete foundation, yours to shape' },
    { href: '/product/hub', title: 'Hub & User Surfaces', description: 'Where your people interact with AI' },
    { href: '/product/builder', title: 'Agent Builder', description: 'Configure, test, and ship agents' },
    { href: '/product/security', title: 'Security & Governance', description: 'Full-stack control over what AI can do' },
    { href: '/product/connectors', title: 'Connectors & Integrations', description: 'Wire AI into your systems' },
    { href: '/product/admin', title: 'Admin & Observability', description: 'See everything, govern everything' },
  ];

  const whyCorralItems = [
    { href: '/why-corral#the-problem', title: 'The Problem', description: 'Enterprise AI has a deployment problem' },
    { href: '/why-corral#the-thesis', title: 'The Thesis', description: 'Getting there shouldn\'t cost $500K' },
    { href: '/why-corral#three-paths', title: 'The Three Paths', description: 'Only one that scales' },
    { href: '/why-corral#for-the-engineer', title: 'For the Engineer', description: 'You\'ve been building this yourself' },
    { href: '/why-corral#sovereignty', title: 'Sovereignty', description: 'Control isn\'t optional — it\'s becoming law' },
  ];

  const useCaseItems = [
    { href: '/use-cases/ops-product', title: 'Ops & Product Teams', description: 'Ship AI shaped to your workflows' },
    { href: '/use-cases/developers', title: 'Developers & Engineers', description: 'Stop rebuilding the stack' },
    { href: '/use-cases/service-providers', title: 'Service Providers & SIs', description: 'A foundation that scales with your practice' },
    { href: '/use-cases/product-companies', title: 'Product Companies', description: 'Build a product, not a platform' },
    { href: '/use-cases/it-security', title: 'IT & Security Teams', description: 'Govern everything, block nothing' },
  ];

  const learnItems = [
    { href: '/docs', title: 'Documentation', description: 'Technical guides and references' },
    { href: '/learn', title: 'Guides', description: 'Walkthroughs and best practices' },
    { href: '/news', title: 'Blog / News', description: 'Updates, insights, and announcements' },
  ];

  // Mobile navigation (flat structure)
  const mobileNavLinks = [
    { href: '/product', label: 'Product' },
    { href: '/why-corral', label: 'Why Corral' },
    { href: '/use-cases/ops-product', label: 'Who can use it?' },
    { href: '/learn', label: 'Learn' },
    { href: '/pricing', label: 'Pricing' },
  ];
</script>

<header class="py-6 border-b border-[#D3D3D3] bg-[#F9F9F4] relative z-20">
  <!-- Logo - centered over left gutter line (desktop) -->
  <a href="/" class="hidden lg:flex absolute left-[9.03%] top-1/2 -translate-y-1/2 -translate-x-1/2 z-10">
    <img src="/shared/images/logos/logo-and-name-black.svg" alt="Corral" class="h-6 w-auto" />
  </a>

  <!-- Nav - centered on page (desktop only) -->
  <NavigationMenu.Root class="hidden lg:flex justify-center w-full max-w-none">
    <NavigationMenu.List>
      <!-- Product mega-menu -->
      <NavigationMenu.Item>
        <NavigationMenu.Trigger>Product</NavigationMenu.Trigger>
        <NavigationMenu.Content>
          <ul class="grid w-[500px] grid-cols-2 gap-1 p-4">
            {#each productItems as item}
              <li>
                <NavigationMenu.Link href={item.href}>
                  <div class="font-medium text-[14px]">{item.title}</div>
                  <p class="text-[12px] text-[#5C5855] font-light">{item.description}</p>
                </NavigationMenu.Link>
              </li>
            {/each}
          </ul>
        </NavigationMenu.Content>
      </NavigationMenu.Item>

      <!-- Why Corral dropdown -->
      <NavigationMenu.Item>
        <NavigationMenu.Trigger>Why Corral</NavigationMenu.Trigger>
        <NavigationMenu.Content>
          <ul class="grid w-[400px] gap-1 p-4">
            {#each whyCorralItems as item}
              <li>
                <NavigationMenu.Link href={item.href}>
                  <div class="font-medium text-[14px]">{item.title}</div>
                  <p class="text-[12px] text-[#5C5855] font-light">{item.description}</p>
                </NavigationMenu.Link>
              </li>
            {/each}
          </ul>
        </NavigationMenu.Content>
      </NavigationMenu.Item>

      <!-- Who can use it? dropdown -->
      <NavigationMenu.Item>
        <NavigationMenu.Trigger>Who can use it?</NavigationMenu.Trigger>
        <NavigationMenu.Content>
          <ul class="grid w-[400px] gap-1 p-4">
            {#each useCaseItems as item}
              <li>
                <NavigationMenu.Link href={item.href}>
                  <div class="font-medium text-[14px]">{item.title}</div>
                  <p class="text-[12px] text-[#5C5855] font-light">{item.description}</p>
                </NavigationMenu.Link>
              </li>
            {/each}
          </ul>
        </NavigationMenu.Content>
      </NavigationMenu.Item>

      <!-- Learn dropdown -->
      <NavigationMenu.Item>
        <NavigationMenu.Trigger>Learn</NavigationMenu.Trigger>
        <NavigationMenu.Content>
          <ul class="grid w-[400px] gap-1 p-4">
            {#each learnItems as item}
              <li>
                <NavigationMenu.Link href={item.href}>
                  <div class="font-medium text-[14px]">{item.title}</div>
                  <p class="text-[12px] text-[#5C5855] font-light">{item.description}</p>
                </NavigationMenu.Link>
              </li>
            {/each}
          </ul>
        </NavigationMenu.Content>
      </NavigationMenu.Item>

      <!-- Pricing - single link -->
      <NavigationMenu.Item>
        <NavigationMenu.Link href="/pricing" class={navigationMenuTriggerStyle()}>
          Pricing
        </NavigationMenu.Link>
      </NavigationMenu.Item>
    </NavigationMenu.List>
  </NavigationMenu.Root>

  <!-- Right side CTA - centered over right gutter line (desktop only) -->
  <div class="hidden lg:flex items-center absolute right-[9.03%] top-1/2 -translate-y-1/2 translate-x-1/2 group">
    <button
      class="bg-black text-white font-mono text-[14px] px-5 py-2.5 hover:bg-gray-800 transition-colors cursor-pointer"
      style="box-shadow: 0px 0px 14.7px 5px rgba(255, 255, 255, 0.35) inset;"
    >
      Try it out!
    </button>

    <!-- Hover Dropdown -->
    <div class="absolute top-full right-0 mt-2 w-[280px] bg-white border border-gray-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
      <div class="p-3">
        <a
          href="/company/contact?subject=Demo+request"
          class="block px-3 py-2.5 font-mono text-[14px] text-black hover:bg-gray-50 transition-colors"
        >
          Schedule a demo
        </a>
        <a
          href="/company/contact"
          class="block px-3 py-2.5 font-mono text-[14px] text-[#5c5855] hover:bg-gray-50 hover:text-black transition-colors"
        >
          Or contact us
        </a>

        <div class="border-t border-gray-200 mt-2 pt-3 px-3">
          <p class="font-mono text-[11px] text-[#9ca3af] uppercase tracking-wider mb-2">
            Coming Soon:
          </p>
          <div class="py-1.5 font-mono text-[14px] text-[#9ca3af] cursor-not-allowed">
            Try in our SaaS
          </div>
          <div class="py-1.5 font-mono text-[14px] text-[#9ca3af] cursor-not-allowed">
            Free Trial on Azure
          </div>
        </div>

        <div class="border-t border-gray-200 mt-2 pt-3 px-3">
          <p class="font-mono text-[11px] text-[#5c5855] uppercase tracking-wider mb-2">
            Free trials & PoCs available in:
          </p>
          <a
            href="/partners"
            class="block px-0 py-2 font-mono text-[14px] text-black hover:text-[#5c5855] transition-colors"
          >
            Design Partner Program →
          </a>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile layout -->
  <div class="flex items-center justify-between px-8 lg:hidden">
    <a href="/">
      <img src="/shared/images/logos/logo-and-name-black.svg" alt="Corral" class="h-6 w-auto" />
    </a>
    <div class="flex items-center gap-3">
      <div class="relative group">
        <button
          class="bg-black text-white font-mono text-[14px] px-5 py-2.5 hover:bg-gray-800 transition-colors cursor-pointer"
          style="box-shadow: 0px 0px 14.7px 5px rgba(255, 255, 255, 0.35) inset;"
        >
          Try it out!
        </button>
        <div class="absolute top-full right-0 mt-2 w-[280px] bg-white border border-gray-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
          <div class="p-3">
            <a href="/company/contact?subject=Demo+request" class="block px-3 py-2.5 font-mono text-[14px] text-black hover:bg-gray-50 transition-colors">
              Schedule a demo
            </a>
            <div class="border-t border-gray-200 mt-2 pt-3 px-3">
              <p class="font-mono text-[11px] text-[#9ca3af] uppercase tracking-wider mb-2">Coming Soon:</p>
              <div class="py-1.5 font-mono text-[14px] text-[#9ca3af] cursor-not-allowed">Try in our SaaS</div>
              <div class="py-1.5 font-mono text-[14px] text-[#9ca3af] cursor-not-allowed">Free Trial on Azure</div>
            </div>
            <div class="border-t border-gray-200 mt-2 pt-3 px-3">
              <p class="font-mono text-[11px] text-[#5c5855] uppercase tracking-wider mb-2">Free trials & PoCs available in:</p>
              <a href="/partners" class="block px-0 py-2 font-mono text-[14px] text-black hover:text-[#5c5855] transition-colors">Design Partner Program →</a>
            </div>
          </div>
        </div>
      </div>
    <Sheet.Root bind:open={mobileMenuOpen}>
      <Sheet.Trigger class="p-2 -mr-2">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="sr-only">Open menu</span>
      </Sheet.Trigger>
      <Sheet.Content side="right" class="w-[300px] bg-[#F9F9F4] p-0">
        <div class="flex flex-col h-full">
          <nav class="flex flex-col pt-16 px-8">
            {#each mobileNavLinks as link}
              <a
                href={link.href}
                class="font-mono text-[16px] text-[#282828] hover:text-black py-4 border-b border-[#D3D3D3] transition-colors"
                onclick={() => mobileMenuOpen = false}
              >
                {link.label}
              </a>
            {/each}
          </nav>
          <div class="mt-auto p-8">
            <a
              href="/company/contact?subject=Demo+request"
              class="bg-black text-white font-mono text-[14px] px-5 py-3 hover:bg-gray-800 transition-colors flex items-center justify-center w-full"
              style="box-shadow: 0px 0px 14.7px 5px rgba(255, 255, 255, 0.35) inset;"
              onclick={() => mobileMenuOpen = false}
            >
              Get in Touch
            </a>
          </div>
        </div>
      </Sheet.Content>
    </Sheet.Root>
    </div>
  </div>
</header>
